import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search, Flag, AlertTriangle, CheckCircle2, ExternalLink, Plus, Store, ArrowRight } from 'lucide-react';
import AppLayout from '../components/AppLayout';
import SyncBar from '../components/SyncBar';
import PetanqueLoader from '../components/PetanqueLoader';
import SupplierDetailDialog from '../components/SupplierDetailDialog';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Select } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { api } from '../api/sangly';
import { money, formatNumber, fmtDate, BLOCKED } from '../lib/format';
import { cn } from '../lib/utils';
import usePollWhileActive from '../hooks/usePollWhileActive';

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'proforma', label: 'Proforma' },
  { key: 'issues', label: 'With issues' },
  { key: 'blocked', label: 'Blocked' },
  { key: 'at_risk', label: 'At risk' },
  { key: 'following', label: 'Following' },
  { key: 'overdue', label: 'Has overdue' },
];

function StatTile({ label, value, sub, active, tone, onClick }) {
  return (
    <button onClick={onClick}
      className={cn('rounded-lg border bg-card px-4 py-3 text-left shadow-card transition-colors hover:border-primary/40',
        active ? 'border-primary/60 ring-1 ring-primary/30' : 'border-border')}>
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={cn('mt-1 text-xl font-semibold tnum', tone)}>{formatNumber(value)}</div>
      {sub && <div className="mt-0.5 text-[11px] text-muted-foreground">{sub}</div>}
    </button>
  );
}

// `unit`: lamptwist (the LampTwist workspace) or calum (the Calum block) — the
// API scopes counts and rows to that business; shared vendors sit in both.
export default function Suppliers({ unit = 'lamptwist' }) {
  const navigate = useNavigate();
  const base = unit === 'calum' ? '/calum/suppliers' : '/suppliers';
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(
    () => (searchParams.get('filter') || '').split(',').filter(Boolean));
  const isActive = (key) => key === 'all' ? filters.length === 0 : filters.includes(key);
  const toggleFilter = (key) => {
    if (key === 'all') { setFilters([]); return; }
    setFilters((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };
  const [q, setQ] = useState('');
  const [ordering, setOrdering] = useState('status');
  // "Sourced" = the vendor has purchase orders in Odoo — a LampTwist notion; Calum's
  // vendors have none, so that filter is off by default in the Calum view.
  const [sourcedOnly, setSourcedOnly] = useState(
    unit === 'calum' ? searchParams.get('sourced') === 'true' : searchParams.get('sourced') !== 'false');
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);

  const params = useMemo(() => {
    const p = { ordering, unit };
    if (q.trim()) p.q = q.trim();
    if (sourcedOnly) p.has_po = 'true';
    if (filters.length) p.filter = filters.join(',');
    return p;
  }, [filters, q, ordering, sourcedOnly, unit]);

  const load = useCallback(async (soft = false) => {
    if (!soft) setLoading(true);
    try {
      const [data, s] = await Promise.all([api.suppliers.list(params), api.invoices.stats({ unit })]);
      setRows(data.results || []);
      setStats(s);
    } finally { setLoading(false); }
  }, [params, unit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.users.list().then(setUsers).catch(() => {}); }, []);
  usePollWhileActive(true, () => load(true), 20000);

  return (
    <AppLayout
      title={unit === 'calum' ? 'Calum · Suppliers' : 'Suppliers'}
      description={unit === 'calum'
        ? 'Calum Brussels vendors - payment issues, status & escalation (bills on the Calum / Horeca accounts)'
        : 'Vendor-level payment issues - problematic cases, status & escalation'}
      syncBar={<SyncBar onSynced={() => load(true)} />}
    >
      {stats && (
        <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
          <StatTile label="Invoices 2026" value={stats.total_count}
                    active={filters.length === 0} onClick={() => setFilters([])} />
          <StatTile label="Paid" value={stats.paid_count} tone="text-emerald-600" />
          <StatTile label="Open" value={stats.open_count} sub={money(stats.open_amount)} />
          <StatTile label="Problematic" value={stats.overdue_count} tone="text-rose-600"
                    active={filters.includes('overdue')} onClick={() => toggleFilter('overdue')} />
          <StatTile label="Due ≤ 7 days" value={stats.due_this_week} tone="text-amber-600" />
        </div>
      )}

      <div className="mb-3 flex flex-col gap-2 lg:flex-row lg:flex-wrap lg:items-center">
        <div className="flex flex-wrap gap-1.5">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => toggleFilter(f.key)}
              className={cn('rounded-md border px-2.5 py-1 text-[12px] font-medium transition-colors',
                isActive(f.key) ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center lg:ml-auto lg:w-auto">
          <label className="flex cursor-pointer items-center gap-1.5 text-[12px] text-muted-foreground"
                 title="Only vendors we place purchase orders with">
            <input type="checkbox" checked={sourcedOnly} onChange={(e) => setSourcedOnly(e.target.checked)}
                   className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]" />
            Sourced suppliers
          </label>
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search vendor…" className="h-9 w-full pl-8 sm:w-52" />
          </div>
          <Select className="h-9 w-full sm:w-44" value={ordering} onChange={(e) => setOrdering(e.target.value)}>
            <option value="status">Sort: Status + € ↓</option>
            <option value="problematic">Problematic ↓</option>
            <option value="amount">Overdue amount ↓</option>
            <option value="due_soon">Due soon ↓</option>
            <option value="-last_payment">Last payment ↓</option>
            <option value="last_payment">Last payment ↑</option>
            <option value="name">Vendor A–Z</option>
          </Select>
          <Button onClick={() => setAddOpen(true)} title="Add a vendor from Odoo that isn't in the list yet (e.g. a proforma-only supplier)">
            <Plus className="h-4 w-4" /> Add vendor
          </Button>
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card shadow-card">
        <Table className="min-w-[1180px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Vendor</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Last reminder</TableHead>
              <TableHead>Gorgias</TableHead>
              <TableHead>Open invoices</TableHead>
              <TableHead className="text-right">Problematic</TableHead>
              <TableHead className="text-right">Due ≤ 7d</TableHead>
              <TableHead className="text-right">Overdue amount</TableHead>
              <TableHead className="text-right">Open</TableHead>
              <TableHead className="text-right">Last payment</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={10} className="h-40 text-center">
                <PetanqueLoader size={34} roll className="mx-auto" />
              </TableCell></TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={10} className="h-40 text-center text-muted-foreground">
                No vendors match these filters.
              </TableCell></TableRow>
            )}
            {!loading && rows.map((s) => {
              const blk = BLOCKED[s.account_status] || BLOCKED[1];
              return (
                <TableRow key={s.id} className="cursor-pointer" onClick={() => navigate(`${base}/${s.id}`)}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{s.name || '-'}</span>
                      {s.effective_unit === 'shared' && <Badge variant="outline" className="text-[10px]" title="Bills for both LampTwist and Calum">Shared</Badge>}
                      {s.is_following && <Flag className="h-3.5 w-3.5 text-primary" title="Following" />}
                      {s.issue_open && !s.resolved_notified && <AlertTriangle className="h-3.5 w-3.5 text-amber-500" title="Open issue" />}
                      {s.issue_open && s.resolved_notified && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" title="Looks resolved" />}
                    </div>
                  </TableCell>
                  <TableCell><Badge variant={blk.variant}>{blk.label}</Badge></TableCell>
                  <TableCell className="text-muted-foreground">{fmtDate(s.last_reminder_date)}</TableCell>
                  <TableCell>
                    {s.gorgias_url ? (
                      <a href={s.gorgias_url} target="_blank" rel="noreferrer"
                         onClick={(e) => e.stopPropagation()}
                         className="inline-flex items-center gap-1 text-primary hover:underline">
                        Ticket <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    ) : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell>
                    <div className="text-[13px]">
                      {s.open_count > 0 ? `${formatNumber(s.open_count)} open · ${money(s.open_amount)}` : '-'}
                    </div>
                    {s.overdue_count > 0 && (
                      <div className="text-[11px] text-rose-600">
                        {formatNumber(s.overdue_count)} overdue · {money(s.overdue_amount)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <span className={cn('font-semibold tnum', s.overdue_count > 0 && 'text-rose-600')}>{s.overdue_count}</span>
                  </TableCell>
                  <TableCell className="text-right tnum">
                    {s.due_soon_count > 0 ? <span className="text-amber-600">{s.due_soon_count}</span> : <span className="text-muted-foreground">-</span>}
                  </TableCell>
                  <TableCell className="text-right font-semibold tnum">{s.overdue_amount ? money(s.overdue_amount) : '-'}</TableCell>
                  <TableCell className="text-right tnum text-muted-foreground">{s.open_count}</TableCell>
                  <TableCell className="text-right text-muted-foreground">{fmtDate(s.last_payment_date)}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      <div className="mt-3 text-[12px] text-muted-foreground">{rows.length} vendor{rows.length === 1 ? '' : 's'}</div>

      <SupplierDetailDialog supplierId={id} open={!!id} scope={filters.includes('overdue') ? 'overdue' : 'open'}
                            since={stats?.focus_since}
                            onClose={() => navigate(base)}
                            onChanged={() => load(true)} users={users} />

      <AddVendorDialog open={addOpen} onClose={() => setAddOpen(false)} base={base}
        onAdded={(sup) => { setAddOpen(false); load(true); navigate(`${base}/${sup.id}`); }} />
    </AppLayout>
  );
}

// Search Odoo for a vendor that has no bill yet (so it isn't in the list) and add
// it to Sangly as Proforma - e.g. a supplier we've only received a proforma from.
function AddVendorDialog({ open, onClose, onAdded, base = '/suppliers' }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => { if (open) { setQ(''); setResults([]); setErr(''); } }, [open]);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setLoading(false); return undefined; }
    setLoading(true);
    const t = setTimeout(async () => {
      try { setResults(await api.suppliers.odooVendors(term)); setErr(''); }
      catch { setErr('Could not reach Odoo to search vendors.'); }
      finally { setLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const add = async (v) => {
    if (v.sangly_id) { onClose(); navigate(`${base}/${v.sangly_id}`); return; }
    setBusyId(v.partner_id); setErr('');
    try {
      const sup = await api.suppliers.addVendor(v.partner_id, v.name);
      onAdded(sup);
    } catch {
      setErr('Could not add the vendor.');
    } finally { setBusyId(null); }
  };

  return (
    <Dialog open={open} onClose={onClose} className="max-w-lg">
      <div className="p-5">
        <h2 className="text-lg font-semibold">Add a vendor</h2>
        <p className="mt-1 text-[13px] text-muted-foreground">
          Search Odoo for a vendor that isn&apos;t in Sangly yet (it has no bill) — add it here to log a
          proforma. It&apos;s added as <b>Proforma</b> and keyed to the Odoo vendor, so a future bill matches it.
        </p>
        <div className="relative mt-3">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Vendor name… (e.g. Established & Sons)" className="h-10 pl-8" />
        </div>
        {err && <div className="mt-2 text-[13px] text-rose-600">{err}</div>}

        <div className="mt-3 max-h-72 overflow-y-auto rounded-lg border border-border divide-y divide-border">
          {loading && (
            <div className="grid h-24 place-items-center"><PetanqueLoader size={26} /></div>
          )}
          {!loading && q.trim().length >= 2 && results.length === 0 && (
            <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">No Odoo vendors match “{q.trim()}”.</div>
          )}
          {!loading && q.trim().length < 2 && (
            <div className="px-3 py-6 text-center text-[13px] text-muted-foreground">Type at least 2 letters to search.</div>
          )}
          {!loading && results.map((v) => (
            <button key={v.partner_id} onClick={() => add(v)} disabled={busyId === v.partner_id}
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left transition-colors hover:bg-muted/50 disabled:opacity-60">
              <Store className="h-4 w-4 shrink-0 text-muted-foreground" />
              <span className="min-w-0 flex-1 truncate text-[14px] font-medium">{v.name}</span>
              {v.sangly_id ? (
                <span className="inline-flex items-center gap-1 text-[12px] text-muted-foreground">
                  Already in Sangly <ArrowRight className="h-3.5 w-3.5" />
                </span>
              ) : busyId === v.partner_id ? (
                <PetanqueLoader size={16} />
              ) : (
                <span className="inline-flex items-center gap-1 text-[12px] font-medium text-primary">
                  <Plus className="h-3.5 w-3.5" /> Add as Proforma
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose} className="text-[13px] font-medium text-muted-foreground hover:underline">Close</button>
        </div>
      </div>
    </Dialog>
  );
}
